<template>
  <Modal
    :title="`Send Pay Link — Invoice #${invoice.invoice_id}`"
    :saving="saving"
    saving-text="Sending..."
    :submit-btn-text="hasActiveLink ? 'Resend Link' : 'Send Link'"
    submit-btn-class="btn btn-sm btn-primary ml-auto"
    :disable-submit="!canSend"
    data-cy="send-pay-link-modal"
    @submit="handleSend"
    @hide-modal="$emit('hide-modal')"
  >
    <div class="form-group mb-0">
      <!-- Channel picker: shown only when more than one channel is enabled for
           the tenant. One row in the menu opens this; the channel lives here so
           the menu never grows a row per channel (QR slots in the same way). -->
      <ButtonToggle
        v-if="channels.length > 1"
        v-model="channel"
        :options="channelOptions"
        class="mb-3"
        data-cy="pay-link-channel"
      />

      <template v-if="channel === 'sms'">
        <!-- Twilio inactive: the send would fail, so show a blocked notice in
             place of the form and disable submit (via canSend). The action stays
             offered and email remains available through the channel toggle. -->
        <div
          v-if="smsBlocked"
          class="notice-message"
          data-cy="pay-link-sms-blocked"
        >
          <i class="far fa-triangle-exclamation mr-1"></i>
          <span>
            Pay by SMS is unavailable because this company's texting account is
            not active. You can still send this invoice by email.
          </span>
        </div>
        <template v-else>
          <p class="mb-3 text-gray-700">
            Text the customer a secure link to pay this invoice. Leave the number
            as-is or edit it before sending.
          </p>
          <TextField
            v-model="phone"
            v-mask="'(###) ###-####'"
            name="phone"
            label="Mobile number"
            placeholder="Customer's mobile number"
            data-cy="pay-link-phone"
          />
          <p
            v-if="phoneChanged"
            class="text-gray-500 mt-1"
            data-cy="pay-link-save-note"
          >
            This number will be added to the customer's additional numbers.
          </p>
        </template>
      </template>

      <template v-else>
        <p class="mb-3 text-gray-700">
          Email the customer a secure link to pay this invoice. Leave the
          address as-is or edit it before sending.
        </p>
        <template v-if="hasCustomerEmail">
          <TextField
            v-model="email"
            name="email"
            type="email"
            label="Email address"
            placeholder="Customer's email address"
            data-cy="pay-link-email"
          />
          <p
            v-if="emailChanged"
            class="text-gray-500 mt-1"
            data-cy="pay-link-save-note"
          >
            This email will be added to the customer's additional emails.
          </p>
        </template>
        <!-- AC5: no email on file — the send is unavailable (submit disabled)
             and we surface a clear path to add one to the customer record. -->
        <div v-else class="notice-message mt-2" data-cy="pay-link-no-email">
          <i class="far fa-circle-info mr-1"></i>
          <span>
            No email on file.
            <a :href="customerPath" data-cy="add-customer-email">
              Add an email to this customer
            </a>
            to send a pay link by email.
          </span>
        </div>
      </template>

      <div v-if="hasActiveLink" class="notice-message mt-2">
        <i class="far fa-circle-info mr-1"></i>
        <span>
          A link is already active. Resending sends a new link and the previous
          one stops working.
        </span>
      </div>
    </div>
  </Modal>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useStore } from "vuex";

import ButtonToggle from "@/components/ButtonToggle.vue";
import Modal from "@/components/Modal.vue";
import TextField from "@/components/TextField.vue";
import { userShowPath } from "@/mixins/path_helpers";
import type { Invoice } from "@/typings/invoice";

const props = defineProps<{ invoice: Invoice }>();
const emit = defineEmits<{
  (e: "hide-modal"): void;
  (e: "sent"): void;
}>();

const store = useStore();
const saving = ref(false);
const phone = ref<string>(props.invoice.customer?.phone ?? "");
const email = ref<string>(props.invoice.customer?.email ?? "");

const phoneSeedDigits = (props.invoice.customer?.phone ?? "").replace(/\D/g, "");
const emailSeed = (props.invoice.customer?.email ?? "").trim().toLowerCase();

// Only send a phone override when the digits actually changed from the seeded
// primary — the mask reformats the display, so compare digits, not the string.
function editedPhoneNumber(): string | undefined {
  const value = phone.value?.trim() ?? "";
  const digits = value.replace(/\D/g, "");
  if (!digits || digits === phoneSeedDigits) return undefined;
  return value;
}

// The save-to-additionals note appears only when the entered value differs from
// the customer's primary — an unchanged primary is reused, not re-added, so the
// note would be misleading. Mirrors the server's dedupe-vs-primary behavior.
const phoneChanged = computed<boolean>(() => editedPhoneNumber() !== undefined);
const emailChanged = computed<boolean>(() => {
  const value = email.value?.trim() ?? "";
  return value.length > 0 && value.toLowerCase() !== emailSeed;
});

const smsEnabled = computed<boolean>(() =>
  store.getters.featureEnabledForTenant("pay_by_sms")
);
const emailEnabled = computed<boolean>(() =>
  store.getters.featureEnabledForTenant("pay_by_email")
);

// The server includes payby / payby_email only when that channel can actually
// send (feature on, processor, payable). revoke_pay_link is present while any
// link is live — it drives the "Resend" label and the already-active notice.
const smsUrl = computed<string | undefined>(() => props.invoice.links?.payby);
const emailUrl = computed<string | undefined>(
  () => props.invoice.links?.payby_email
);
const hasCustomerEmail = computed<boolean>(() =>
  Boolean(props.invoice.customer?.email)
);
const hasActiveLink = computed<boolean>(() =>
  Boolean(props.invoice.links?.revoke_pay_link)
);
// The SMS action stays offered even when the tenant's Twilio is inactive; the
// server flags it so we replace the SMS form with a blocked notice and disable
// submit (via canSend) rather than let the send fail. Email is unaffected.
const smsBlocked = computed<boolean>(() =>
  Boolean(props.invoice.pay_by_sms_blocked)
);

const channels = computed<string[]>(() => {
  const list: string[] = [];
  if (smsEnabled.value) list.push("sms");
  if (emailEnabled.value) list.push("email");
  return list;
});

const channelOptions = computed(() =>
  channels.value.map((c) => ({ label: c === "sms" ? "SMS" : "Email", value: c }))
);

// Default to the first channel that can actually send, falling back to the
// first available so the toggle always has a selection. A Twilio-inactive
// tenant skips SMS so the modal opens on the sendable email channel.
function firstSendableChannel(): string {
  if (smsEnabled.value && smsUrl.value && !smsBlocked.value) return "sms";
  if (emailEnabled.value && emailUrl.value && hasCustomerEmail.value) {
    return "email";
  }
  return channels.value[0] ?? "sms";
}
const channel = ref<string>(firstSendableChannel());

const canSend = computed<boolean>(() => {
  if (channel.value === "sms") {
    return Boolean(smsUrl.value) && !smsBlocked.value;
  }
  if (channel.value === "email") {
    return Boolean(emailUrl.value) && hasCustomerEmail.value;
  }
  return false;
});

const customerPath = computed<string>(() => {
  // The API serializes the customer's id as customer_id (see _customer_short);
  // id is the fallback for any payload that carries it instead.
  const id = props.invoice.customer?.customer_id ?? props.invoice.customer?.id;
  return id && id > 0 ? userShowPath(id) : "#";
});

// On failure the global response interceptor (vuex/interceptors.js) already
// shows the endpoint's error message, so the catch here only stops the success
// path (modal stays open) — it must not toast again or the user sees the same
// error twice.
async function handleSend(): Promise<void> {
  if (!canSend.value || saving.value) return;
  saving.value = true;
  try {
    if (channel.value === "sms") {
      await store.dispatch("sendPayLink", {
        url: smsUrl.value,
        phoneNumber: editedPhoneNumber()
      });
      (window as any).notify.success("Pay link sent.");
    } else {
      await store.dispatch("sendPayLinkEmail", {
        url: emailUrl.value,
        email: email.value?.trim() || undefined
      });
      (window as any).notify.success("Pay link emailed.");
    }
    emit("sent");
    emit("hide-modal");
  } catch {
    // error surfaced globally; keep the modal open
  } finally {
    saving.value = false;
  }
}
</script>
